/**
 * The app's AI host: its voice modes, avatar and limits, and what customers said to it. Talks to the /appai group,
 * the admin.php "AI modes" and "Customer support" tabs moved into Connect (2026-10-15). Non-namespaced like every
 * other module.
 */
const notifyError = (commit, message) =>
  commit("NOTIFICATION_MESSAGE", { status: -1, title: "App AI", message });

const form = (obj) => {
  const p = new URLSearchParams();
  Object.keys(obj).forEach((k) => p.append(k, obj[k]));
  return p;
};

const state = () => ({
  settings: null, // { modes, voice_orb_enabled, more_qr_enabled, simli_face_id, voice_session_limits }
  settingsError: "",
  people: [],
  peopleLoaded: false,
  peopleError: "",
  conversation: null, // { customer, messages }
});

const mutations = {
  RESET_APPAI(s) { Object.assign(s, state()); },
  SET_AI_SETTINGS(s, v) { s.settings = v; s.settingsError = ""; },
  SET_AI_SETTINGS_ERROR(s, m) { s.settingsError = m; },
  SET_AI_PEOPLE(s, list) { s.people = list || []; s.peopleLoaded = true; s.peopleError = ""; },
  SET_AI_PEOPLE_ERROR(s, m) { s.peopleError = m; s.peopleLoaded = true; },
  SET_AI_CONVERSATION(s, v) { s.conversation = v; },
};

const pickSettings = (d) => ({
  modes: d.modes, voice_orb_enabled: d.voice_orb_enabled, more_qr_enabled: d.more_qr_enabled,
  simli_face_id: d.simli_face_id, voice_session_limits: d.voice_session_limits,
});

const actions = {
  async getAppAiSettings({ commit }) {
    commit("LOADING", { isActiveStatus: true });
    try {
      const { data } = await window.$http.axios.get("appai/settings");
      if (data.status) commit("SET_AI_SETTINGS", pickSettings(data));
      else { notifyError(commit, data.message); commit("SET_AI_SETTINGS_ERROR", data.message || "The server refused the request."); }
    } catch (e) {
      commit("SET_AI_SETTINGS_ERROR", "Could not reach the app AI service.");
    } finally {
      commit("LOADING", { isActiveStatus: false });
    }
  },

  async saveAppAiSettings({ commit }, settings) {
    try {
      const { data } = await window.$http.axios.post("appai/settings/save", form({ settings: JSON.stringify(settings) }));
      if (data.status) {
        commit("SET_AI_SETTINGS", pickSettings(data));
        commit("NOTIFICATION_MESSAGE", { status: 1, title: "App AI", message: "Saved. Customers get it from their next voice session." });
        return true;
      }
      notifyError(commit, data.message); return false;
    } catch (e) { notifyError(commit, "Could not save the settings."); return false; }
  },

  /** The first load shows the global loader; searches pass silent so the page stays usable. */
  async getAiPeople({ commit }, { q = "", silent = false } = {}) {
    if (!silent) commit("LOADING", { isActiveStatus: true });
    try {
      const { data } = await window.$http.axios.get("appai/chats/people", { params: { q } });
      if (data.status) commit("SET_AI_PEOPLE", data.people);
      else { notifyError(commit, data.message); commit("SET_AI_PEOPLE_ERROR", data.message || "The server refused the request."); }
    } catch (e) {
      commit("SET_AI_PEOPLE_ERROR", "Could not reach the app AI service.");
    } finally {
      if (!silent) commit("LOADING", { isActiveStatus: false });
    }
  },

  async getAiConversation({ commit }, cID) {
    commit("SET_AI_CONVERSATION", null);
    try {
      const { data } = await window.$http.axios.get("appai/chats/conversation", { params: { cID } });
      if (data.status) commit("SET_AI_CONVERSATION", { customer: data.customer, messages: data.messages || [] });
      else notifyError(commit, data.message);
    } catch (e) { notifyError(commit, "Could not load the conversation."); }
  },
};

export default { state, mutations, actions };
